import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

/** Buddy behaviour stored on the signed-in user (#3899). */
export const ASSISTANT_MODES = ['ask', 'guide', 'do'] as const;
export const assistantModeSchema = z.enum(ASSISTANT_MODES);
export type AssistantMode = z.infer<typeof assistantModeSchema>;

const assistantPreferencesSchema = z.object({
  assistantMode: assistantModeSchema,
});

export type AssistantPreferences = z.infer<typeof assistantPreferencesSchema>;

export function isAssistantMode(value: string): value is AssistantMode {
  return assistantModeSchema.safeParse(value).success;
}

function parsePreferences(value: unknown): AssistantPreferences {
  const parsed = assistantPreferencesSchema.safeParse(value);
  if (!parsed.success) {
    throw new Error('Invalid assistant preferences');
  }
  return parsed.data;
}

/** The signed-in user's Buddy mode (`GET /assistant/preferences`). */
export function useAssistantPreferences(enabled = true) {
  return useQuery({
    queryKey: queryKeys.assistant.preferences(),
    queryFn: async () => parsePreferences(await api.get<unknown>('/assistant/preferences')),
    enabled,
  });
}

/** Save the signed-in user's Buddy mode (`PUT /assistant/preferences`). */
export function useUpdateAssistantPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assistantMode: AssistantMode) => {
      const mode = assistantModeSchema.parse(assistantMode);
      return parsePreferences(
        await api.put<unknown>('/assistant/preferences', { assistantMode: mode }),
      );
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.assistant.preferences(), data);
    },
  });
}
